/* eslint-disable  @typescript-eslint/no-explicit-any */
import {
    deviceId,
    loginRouteCodespot,
    maxRequestRetry,
} from './toya/apiConstantsCodespot'
import { getErrorMessage } from '@/shared/lib'
import { createRequestData } from './prepareRequestData'
import {
    refreshTokenKeyword,
    saveToLocalStorage,
    sessionTokenLsKeyword,
} from '@/shared/localStorage/localStorageRepository'
import { handle401Error, handleHttpErrorRetry } from '@/api/errorHandlers'
import { useAppStore } from '@/shared/store'
import { getApiService } from '@/api/getApiService'

const baseUrl = process.env.REACT_APP_BASE_URL

export const DelayedPromise = (delayMillis: number) =>
    new Promise((resolve) => setTimeout(resolve, delayMillis))

export type RequestMethod = 'POST' | 'PUT' | 'GET' | 'DELETE' | 'PATCH'

const retryWithNewToken = async (
    originalRequest: (requestData: any) => Promise<Response>,
    requestMethod: RequestMethod,
    profileId?: string,
    requestBody?: string,
    requestHeaders?: object
) => {
    const api = getApiService()
    try {
        const newAccessToken = await handle401Error()
        const { id } = useAppStore.getState().actualProfile || {}
        if (newAccessToken) {
            const newRequestData = api?.createRequestData(
                requestMethod,
                profileId || id,
                newAccessToken,
                requestBody,
                requestHeaders
            )
            return await originalRequest(newRequestData)
        }
    } catch (error) {
        localStorage.removeItem(refreshTokenKeyword)
        localStorage.removeItem(sessionTokenLsKeyword)
        console.error('Refresh token failure detected')

        return null
    }
    throw new Error('Failed to refresh token')
}

export const request = async (
    requestMethod: RequestMethod,
    route: string,
    authToken?: string | null,
    includeBaseUrl = true,
    requestBody?: string,
    profileId?: string,
    requestHeaders?: object
) => {
    let jsonData
    let maxAge
    let eTag
    let requestTime

    const { id } = useAppStore.getState().actualProfile || {}
    const requestData = createRequestData(
        requestMethod,
        profileId || id,
        authToken,
        requestBody,
        requestHeaders
    )

    const originalRequest = async (requestData: any) => {
        const url =
            includeBaseUrl === undefined || includeBaseUrl
                ? `${baseUrl}${route}`
                : `${route}`
        const response = await fetch(url, requestData)
        maxAge = response?.headers.get('cache-control')
        eTag = response?.headers.get('etag')
        requestTime = Date.now()
        return response
    }

    try {
        let response: Response | null = await originalRequest(requestData)

        if (response && response.status === 401) {
            response = await retryWithNewToken(
                originalRequest,
                requestMethod,
                profileId || id,
                requestBody,
                requestHeaders
            )
        }
        maxAge = response?.headers.get('cache-control')
        eTag = response?.headers.get('etag')
        requestTime = Date.now()

        if (response && response.status !== 200) {
            jsonData = await handleHttpErrorRetry(
                response.url,
                requestData,
                maxRequestRetry
            )
        } else {
            const contentType = response?.headers.get('content-type')
            if (contentType && contentType.includes('application/json')) {
                const text = await response?.text()
                jsonData = text ? JSON.parse(text) : {}
            } else {
                jsonData = await response?.text()
            }
        }
    } catch (error) {
        getErrorMessage('request', error)
        throw error
    }

    return { jsonData, maxAge, eTag, requestTime }
}

export const loginRequest = async (
    requestMethod: RequestMethod,
    route: string,
    authToken?: string | null,
    includeBaseUrl?: boolean,
    requestBody?: string
) => {
    const requestData = createRequestData(
        requestMethod,
        undefined,
        authToken,
        requestBody
    )

    try {
        const url =
            includeBaseUrl === undefined || includeBaseUrl
                ? `${baseUrl}${route}`
                : `${route}`

        const response = await fetch(`${url}`, requestData)
        if (!response?.ok) {
            return null
        }
        return await response.json()
    } catch (error) {
        getErrorMessage('request', error)
        throw error
    }
}

export const signIn = async (userName: string, password: string) => {
    try {
        const loginResponse = await loginRequest(
            'POST',
            loginRouteCodespot,
            undefined,
            true,
            JSON.stringify({
                login: userName,
                password,
                deviceId,
            })
        )

        if (!loginResponse) {
            return null
        }

        saveToLocalStorage(sessionTokenLsKeyword, loginResponse.token)
        saveToLocalStorage(refreshTokenKeyword, loginResponse.refreshToken)

        return loginResponse.token
    } catch (error) {
        getErrorMessage('signIn', error)
        throw error
    }
}

export const refreshAccessToken = async () => {
    const refreshToken = localStorage.getItem(refreshTokenKeyword)

    if (!refreshToken) {
        getErrorMessage(
            'No refresh token available apiClient',
            'No refresh token available'
        )
        return null
    }

    try {
        const response = await fetch(`${baseUrl}${loginRouteCodespot}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ refreshToken, deviceId }),
        })
        if (
            !response.ok ||
            response.status === 400 ||
            response.status === 401
        ) {
            getErrorMessage(
                'refreshAccessToken apiClient',
                'Refresh token is invalid or expired'
            )
            localStorage.removeItem(refreshTokenKeyword)
            localStorage.removeItem(sessionTokenLsKeyword)
            return null
        }

        const data = await response.json()

        saveToLocalStorage(sessionTokenLsKeyword, data.token)
        saveToLocalStorage(refreshTokenKeyword, data.refreshToken)
        return data.token
    } catch (error) {
        getErrorMessage('refreshAccessToken apiClient', error)
        return null
    }
}
